import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import posthog from 'posthog-js';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { FEATURES, type FeatureKey } from './registry';
import type { FeatureDefinition } from './types';
import { isEnvEnabled } from './envFlags';

/** Account entitlement keys (charging flexibility opt-out — D13). */
export type EntitlementKey =
  | 'sdr_voice'
  | 'esignature'
  | 'marketing_hub'
  | 'ai_ops'
  | 'agent_messaging';

interface FeatureFlagsContextType {
  isEnabled: (key: FeatureKey) => boolean;
  /**
   * Whether the account is entitled to a chargeable extra (opt-out UX layer).
   * Used by action-level gates that aren't full features/routes (e.g. the
   * "Enviar para assinatura" button → 'esignature'). Mirrors the per-feature
   * defaults (extras OFF, ai_ops ON) while loading. NOT the security boundary.
   */
  isEntitled: (key: EntitlementKey) => boolean;
  loading: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  isEnabled: () => false,
  isEntitled: () => false,
  loading: true,
});

interface FeatureFlagsAggregate {
  featureFlags: Record<string, boolean>;
  entitlements: Record<string, boolean>;
}

/**
 * Default account entitlements when the BFF aggregate omits them (legacy
 * payload, 404, etc.) — mirrors janus-billing `account_entitlements`:
 * real extras default OFF (opt-in), core ai_ops ON. Keeps the opt-out UX
 * fail-safe (an extra is hidden until billing says it's enabled).
 */
const DEFAULT_ENTITLEMENTS: Record<string, boolean> = {
  sdr_voice: false,
  esignature: false,
  marketing_hub: false,
  ai_ops: true,
  // Agente de Mensageria (R$ 249/usuário coberto/mês). Opt-in, como todo
  // extra cobrável: janus-billing declara `agent_messaging` em
  // `_OPT_IN_FEATURES` com `_FEATURE_DEFAULT_ENABLED = False`. Default `true`
  // aqui mostraria a tela de contratação para quem o billing recusa com
  // 403 FEATURE_NOT_ENABLED.
  agent_messaging: false,
};

async function fetchTenantFlags(tenantId: string): Promise<FeatureFlagsAggregate> {
  const empty: FeatureFlagsAggregate = {
    featureFlags: {},
    entitlements: { ...DEFAULT_ENTITLEMENTS },
  };
  try {
    const { data } = await api.get<{
      featureFlags?: Record<string, boolean>;
      entitlements?: Record<string, boolean>;
    } & Record<string, boolean>>(`/tenants/${tenantId}/feature-flags`);
    if (data && typeof data === 'object' && 'featureFlags' in data) {
      return {
        featureFlags: (data.featureFlags ?? {}) as Record<string, boolean>,
        entitlements: { ...DEFAULT_ENTITLEMENTS, ...(data.entitlements ?? {}) },
      };
    }
    // Legacy shape: the whole body is the flat flags map (no envelope).
    return { featureFlags: (data ?? {}) as Record<string, boolean>, entitlements: { ...DEFAULT_ENTITLEMENTS } };
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return empty;
    throw err;
  }
}

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const { data: aggregate, isLoading: tenantLoading } = useQuery({
    queryKey: ['tenant-features', tenantId],
    queryFn: () => fetchTenantFlags(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const tenantFlags = aggregate?.featureFlags;
  const entitlements = aggregate?.entitlements;

  const [posthogReady, setPosthogReady] = useState(false);

  useEffect(() => {
    try {
      posthog.onFeatureFlags(() => setPosthogReady(true));
    } catch {
      setPosthogReady(true);
    }
  }, []);

  const isEnabled = useCallback(
    (key: FeatureKey): boolean => {
      const def: FeatureDefinition = FEATURES[key];
      if (!def) return false;

      // RBAC guard, shared by every path below. Mirrors the original
      // semantics exactly: roles are only enforced when the definition
      // declares them AND we know who the user is.
      const roleDenied = !!def.roles && !!user && !def.roles.includes(user.role);

      // Tenant explicit false is the EMERGENCY BRAKE: it blocks immediately,
      // ahead of the entitlement, the env gate and the defaults, so a paid but
      // broken module can be killed per tenant without a rebuild.
      const tenantValue = def.tenantKey && tenantFlags ? tenantFlags[def.tenantKey] : undefined;
      if (tenantValue === false) return false;

      // Account entitlement gate (charging flexibility opt-out — D13).
      // This is purely the UX opt-out layer (hide + upsell); billing's 403
      // FEATURE_NOT_ENABLED remains the security boundary, and the route-level
      // RoleGuard remains the access control. Additive to RBAC.
      if (def.entitlementKey) {
        const map = entitlements ?? DEFAULT_ENTITLEMENTS;
        const granted = map[def.entitlementKey] ?? DEFAULT_ENTITLEMENTS[def.entitlementKey] ?? false;

        // NOT granted → hidden + upsell. Fail-safe: while entitlements are
        // still loading or the key is absent we fall back to the opt-in
        // default for that key (extras OFF) so a paid-extra UI never flashes
        // on before billing confirms it. Unchanged behaviour.
        if (!granted) return false;

        // GRANTED → the entitlement is SUFFICIENT ("quem paga, vê" — GC-08.1).
        // Treated exactly like an explicit tenant `true`: an authoritative
        // remote YES bypasses the env gate. This is the fix — previously the
        // entitlement was only a NECESSARY condition and execution fell
        // through to `isEnvEnabled(key)`, which for a chargeable extra
        // (defaultEnabled=false, e.g. `mkt` → marketing_hub) resolved to false
        // unless the deploy was rebuilt with VITE_FF_*=true. Result: an
        // account that had PAID for the module still could not see it.
        // `defaultEnabled` stays false in the registry on purpose — it is the
        // right answer for an account that did NOT buy the extra; the defect
        // was the order of evaluation, not the default.
        return !roleDenied;
      }

      // Tenant explicit true bypasses the env gate so per-tenant activation
      // works without requiring a rebuild with VITE_FF_* set.
      if (tenantValue === true) {
        return !roleDenied;
      }

      if (!isEnvEnabled(key)) return false;

      if (roleDenied) return false;

      if (def.posthogKey && posthogReady) {
        const phValue = posthog.isFeatureEnabled(def.posthogKey);
        if (phValue === false) return false;
      }

      // Reaching here means the env gate already resolved to ENABLED:
      // isEnvEnabled() returns the VITE_FF_* override when set (true/false),
      // and falls back to defaultEnabled only when unset. So `true` here
      // honors an explicit VITE_FF_*=true on a dark-launched feature
      // (defaultEnabled=false) — previously it wrongly fell back to
      // defaultEnabled and the env override could never turn a feature ON.
      return true;
    },
    [tenantFlags, entitlements, user, posthogReady],
  );

  const isEntitled = useCallback(
    (key: EntitlementKey): boolean => {
      const map = entitlements ?? DEFAULT_ENTITLEMENTS;
      return map[key] ?? DEFAULT_ENTITLEMENTS[key] ?? false;
    },
    [entitlements],
  );

  const loading = !!tenantId && tenantLoading;

  const value = useMemo(
    () => ({ isEnabled, isEntitled, loading }),
    [isEnabled, isEntitled, loading],
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlagsContext() {
  return useContext(FeatureFlagsContext);
}
